/**
 * RadioTrader quotation system
 *
 * Stores quotation products separately from the Shopify basket.
 * Captures:
 * - Shopify product and variant
 * - Quantity
 * - SKU
 * - Current base price
 * - Easify Product Options selections
 * - Easify add-on prices
 */

(() => {
  const STORAGE_KEY = "radiotrader_quote_items";

  const config = window.RadioTraderQuoteConfig || {};
  const easifyOptionsUrl = config.easifyOptionsUrl || "";
  const currencyCode = config.currency || "GBP";

  let easifyDataPromise = null;

  function getQuoteItems() {
    try {
      const storedItems = localStorage.getItem(STORAGE_KEY);
      const parsedItems = storedItems
        ? JSON.parse(storedItems)
        : [];

      return Array.isArray(parsedItems)
        ? parsedItems
        : [];
    } catch (error) {
      console.error(
        "Unable to read quote list:",
        error,
      );

      return [];
    }
  }

  function saveQuoteItems(items) {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(items),
      );

      document.dispatchEvent(
        new CustomEvent(
          "radiotrader:quote-updated",
          {
            detail: {
              items,
              itemCount:
                getQuoteItemCount(items),
            },
          },
        ),
      );
    } catch (error) {
      console.error(
        "Unable to save quote list:",
        error,
      );
    }
  }

  function getQuoteItemCount(
    items = getQuoteItems(),
  ) {
    return items.reduce(
      (total, item) => {
        const quantity =
          Number.parseInt(
            item.quantity,
            10,
          );

        return (
          total +
          (
            Number.isFinite(quantity) &&
            quantity > 0
              ? quantity
              : 1
          )
        );
      },
      0,
    );
  }

  function normaliseText(value) {
    return String(value || "")
      .replace(/\u00a0/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  }

  function normaliseImageUrl(image) {
    if (!image) {
      return "";
    }

    if (typeof image === "string") {
      return image;
    }

    return image.src || "";
  }

  function parseDisplayedPrice(value) {
    const text = String(value || "")
      .replace(/,/g, "");

    const match = text.match(
      /(?:£|GBP\s*)\s*(-?\d+(?:\.\d{1,2})?)/i,
    );

    if (!match) {
      return 0;
    }

    const amount =
      Number.parseFloat(match[1]);

    return Number.isFinite(amount)
      ? Math.round(amount * 100)
      : 0;
  }

  function formatMoney(
    amountInPence,
  ) {
    try {
      return new Intl.NumberFormat(
        "en-GB",
        {
          style: "currency",
          currency: currencyCode,
        },
      ).format(
        Number(amountInPence || 0) /
          100,
      );
    } catch (error) {
      return `£${(
        Number(amountInPence || 0) /
        100
      ).toFixed(2)}`;
    }
  }

  async function loadEasifyData() {
    if (!easifyOptionsUrl) {
      return null;
    }

    if (!easifyDataPromise) {
      easifyDataPromise = fetch(
        easifyOptionsUrl,
        {
          method: "GET",
          headers: {
            Accept:
              "application/json",
          },
        },
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              `Easify option file failed: ${response.status}`,
            );
          }

          return response.json();
        })
        .catch((error) => {
          console.error(
            "Unable to load Easify pricing data:",
            error,
          );

          return null;
        });
    }

    return easifyDataPromise;
  }

  async function getProductData(
    handle,
  ) {
    if (!handle) {
      throw new Error(
        "Product handle is missing.",
      );
    }

    const root =
      window.Shopify &&
      window.Shopify.routes
        ? window.Shopify.routes.root
        : "/";

    const response = await fetch(
      `${root}products/${encodeURIComponent(handle)}.js`,
      {
        method: "GET",
        headers: {
          Accept:
            "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error(
        `Product request failed: ${response.status}`,
      );
    }

    return response.json();
  }

  function getProductForm(button) {
    const productFormId =
      button.dataset.productFormId;

    if (productFormId) {
      const form =
        document.getElementById(
          productFormId,
        );

      if (form) {
        return form;
      }
    }

    const productBlock =
      button.closest(
        ".product-block-product-form",
      );

    if (!productBlock) {
      return null;
    }

    return productBlock.querySelector(
      'form[data-type="add-to-cart-form"]',
    );
  }

  function getSelectedVariantId(
    form,
  ) {
    if (!form) {
      return null;
    }

    const variantInput =
      form.querySelector(
        'input[name="id"]',
      );

    return variantInput
      ? Number(variantInput.value)
      : null;
  }

  function getSelectedQuantity(
    form,
  ) {
    if (!form) {
      return 1;
    }

    const quantityInput =
      form.querySelector(
        'input[name="quantity"]',
      );

    const quantity =
      quantityInput
        ? Number.parseInt(
            quantityInput.value,
            10,
          )
        : 1;

    return (
      Number.isFinite(quantity) &&
      quantity > 0
        ? quantity
        : 1
    );
  }

  function getPropertyLabel(
    field,
  ) {
    const name =
      field.getAttribute("name") ||
      "";

    const propertyMatch =
      name.match(
        /^properties\[(.+)]$/,
      );

    if (propertyMatch) {
      return propertyMatch[1].trim();
    }

    if (field.id) {
      const escapedId =
        window.CSS &&
        typeof window.CSS.escape ===
          "function"
          ? window.CSS.escape(field.id)
          : field.id.replace(
              /["\\]/g,
              "\\$&",
            );

      const associatedLabel =
        document.querySelector(
          `label[for="${escapedId}"]`,
        );

      if (associatedLabel) {
        return associatedLabel
          .textContent
          .replace(/\*/g, "")
          .replace(/\s+/g, " ")
          .trim();
      }
    }

    return "";
  }

  function getFieldValue(field) {
    if (!field) {
      return "";
    }

    const tagName =
      field.tagName.toLowerCase();

    const type = String(
      field.type || "",
    ).toLowerCase();

    if (
      (
        type === "radio" ||
        type === "checkbox"
      ) &&
      !field.checked
    ) {
      return "";
    }

    if (tagName === "select") {
      return Array.from(
        field.selectedOptions,
      )
        .map((option) =>
          option.textContent.trim(),
        )
        .filter(Boolean)
        .join(", ");
    }

    return String(
      field.value || "",
    ).trim();
  }

  function cleanText(value) {
    return String(value || "")
      .replace(/\*/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  function cleanSelectedValue(
    element,
  ) {
    const clone =
      element.cloneNode(true);

    clone
      .querySelectorAll(
        [
          ".po-select-selectedPrice",
          "[class*='selectedPrice']",
          "[class*='SelectedPrice']",
        ].join(","),
      )
      .forEach((priceElement) => {
        priceElement.remove();
      });

    return cleanText(
      clone.textContent,
    );
  }

  function collectPropertySelections(
    form,
  ) {
    if (!form) {
      return [];
    }

    const selections = [];

    form
      .querySelectorAll(
        '[name^="properties["]',
      )
      .forEach((field) => {
        const label =
          getPropertyLabel(field);

        const value =
          getFieldValue(field);

        if (!value) {
          return;
        }

        selections.push({
          label:
            label ||
            "Selected option",

          value,

          displayedPrice: 0,

          source:
            "line-item-property",
        });
      });

    return selections;
  }

  function getEasifyOptionLabel(
    optionRoot,
  ) {
    if (!optionRoot) {
      return "Selected option";
    }

    const labelElement =
      optionRoot.querySelector(
        [
          ".po-label-label",
          "[class*='po-label-label']",
          "legend",
        ].join(","),
      );

    if (!labelElement) {
      return "Selected option";
    }

    return (
      cleanText(
        labelElement.textContent,
      ) ||
      "Selected option"
    );
  }

  function elementIsVisible(
    element,
  ) {
    if (!element) {
      return false;
    }

    const style =
      window.getComputedStyle(
        element,
      );

    const rect =
      element.getBoundingClientRect();

    return (
      style.display !== "none" &&
      style.visibility !==
        "hidden" &&
      Number.parseFloat(
        style.opacity || "1",
      ) > 0 &&
      rect.width > 0 &&
      rect.height > 0
    );
  }

  function parseRgbColour(
    colour,
  ) {
    const match =
      String(colour || "").match(
        /rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)(?:[,\s/]+([\d.]+))?\s*\)/i,
      );

    if (!match) {
      return null;
    }

    return {
      red: Number(match[1]),
      green: Number(match[2]),
      blue: Number(match[3]),
      alpha:
        match[4] === undefined
          ? 1
          : Number(match[4]),
    };
  }

  function getColourDarkness(
    colour,
  ) {
    const rgb =
      parseRgbColour(colour);

    if (
      !rgb ||
      rgb.alpha === 0
    ) {
      return 0;
    }

    const brightness =
      rgb.red * 0.299 +
      rgb.green * 0.587 +
      rgb.blue * 0.114;

    return 255 - brightness;
  }

  function getButtonSelectionScore(
    item,
  ) {
    if (
      !item ||
      !elementIsVisible(item)
    ) {
      return -1;
    }

    let score = 0;

    const itemStyle =
      window.getComputedStyle(
        item,
      );

    score += getColourDarkness(
      itemStyle.backgroundColor,
    );

    Array.from(
      item.querySelectorAll("*"),
    ).forEach((child) => {
      if (
        !elementIsVisible(child)
      ) {
        return;
      }

      const style =
        window.getComputedStyle(
          child,
        );

      const darkness =
        getColourDarkness(
          style.backgroundColor,
        );

      if (darkness > 80) {
        score += darkness;
      }

      const className =
        String(
          child.className || "",
        ).toLowerCase();

      if (
        className.includes(
          "selected",
        ) &&
        !className.includes(
          "select-none",
        )
      ) {
        score += 500;
      }

      if (
        className.includes(
          "active",
        ) ||
        className.includes(
          "checked",
        )
      ) {
        score += 500;
      }
    });

    const ariaChecked =
      item.getAttribute(
        "aria-checked",
      );

    const ariaSelected =
      item.getAttribute(
        "aria-selected",
      );

    const dataState =
      item.getAttribute(
        "data-state",
      );

    if (
      ariaChecked === "true" ||
      ariaSelected === "true" ||
      dataState === "checked" ||
      dataState === "selected" ||
      dataState === "on"
    ) {
      score += 1000;
    }

    return score;
  }

  function getRadioSelectionScore(
    item,
  ) {
    if (
      !item ||
      !elementIsVisible(item)
    ) {
      return -1;
    }

    let score = 0;

    const ariaChecked =
      item.getAttribute(
        "aria-checked",
      );

    const ariaSelected =
      item.getAttribute(
        "aria-selected",
      );

    const dataState =
      item.getAttribute(
        "data-state",
      );

    if (
      ariaChecked === "true" ||
      ariaSelected === "true" ||
      dataState === "checked" ||
      dataState === "selected" ||
      dataState === "on"
    ) {
      score += 1000;
    }

    Array.from(
      item.querySelectorAll("*"),
    ).forEach((child) => {
      if (
        !elementIsVisible(child)
      ) {
        return;
      }

      const rect =
        child.getBoundingClientRect();

      const style =
        window.getComputedStyle(
          child,
        );

      const className =
        String(
          child.className || "",
        ).toLowerCase();

      if (
        className.includes(
          "selected",
        ) &&
        !className.includes(
          "select-none",
        )
      ) {
        score += 500;
      }

      if (
        className.includes(
          "active",
        ) ||
        className.includes(
          "checked",
        )
      ) {
        score += 500;
      }

      if (
        rect.width > 0 &&
        rect.height > 0 &&
        rect.width <= 22 &&
        rect.height <= 22
      ) {
        const darkness =
          getColourDarkness(
            style.backgroundColor,
          );

        if (darkness > 100) {
          score += darkness * 3;
        }
      }
    });

    return score;
  }

  function findSelectedButtonChoice(
    items,
  ) {
    const itemList =
      Array.from(items || []);

    if (!itemList.length) {
      return null;
    }

    const scoredItems =
      itemList
        .map((item) => ({
          item,
          score:
            getButtonSelectionScore(
              item,
            ),
        }))
        .sort(
          (first, second) =>
            second.score -
            first.score,
        );

    if (
      !scoredItems.length ||
      scoredItems[0].score < 0
    ) {
      return null;
    }

    return scoredItems[0].item;
  }

  function findSelectedRadioChoice(
    items,
  ) {
    const itemList =
      Array.from(items || []);

    if (!itemList.length) {
      return null;
    }

    const scoredItems =
      itemList
        .map((item) => ({
          item,
          score:
            getRadioSelectionScore(
              item,
            ),
        }))
        .sort(
          (first, second) =>
            second.score -
            first.score,
        );

    if (
      !scoredItems.length ||
      scoredItems[0].score <= 0
    ) {
      return null;
    }

    return scoredItems[0].item;
  }

  function collectVisibleEasifySelections() {
    const selections = [];

    document
      .querySelectorAll(
        ".po-option-root",
      )
      .forEach((optionRoot) => {
        const label =
          getEasifyOptionLabel(
            optionRoot,
          );

        const selectedDropdown =
          optionRoot.querySelector(
            ".po-select-selectedValue",
          );

        if (selectedDropdown) {
          const value =
            cleanSelectedValue(
              selectedDropdown,
            );

          const priceElement =
            selectedDropdown.querySelector(
              ".po-select-selectedPrice",
            );

          if (value) {
            selections.push({
              label,
              value,

              displayedPrice:
                parseDisplayedPrice(
                  priceElement
                    ? priceElement
                        .textContent
                    : "",
                ),

              source:
                "easify-dropdown",
            });
          }

          return;
        }

        const buttonItems =
          optionRoot.querySelectorAll(
            ".po-buttonOption-optionItem",
          );

        if (buttonItems.length) {
          const selectedItem =
            findSelectedButtonChoice(
              buttonItems,
            );

          if (selectedItem) {
            const valueElement =
              selectedItem.querySelector(
                [
                  ".po-buttonOption-label",
                  "[class*='po-buttonOption-label']",
                ].join(","),
              );

            const value =
              cleanText(
                valueElement
                  ? valueElement
                      .textContent
                  : selectedItem
                      .textContent,
              );

            if (value) {
              selections.push({
                label,
                value,

                displayedPrice:
                  parseDisplayedPrice(
                    selectedItem
                      .textContent,
                  ),

                source:
                  "easify-button-option",
              });
            }
          }

          return;
        }

        const radioItems =
          optionRoot.querySelectorAll(
            ".po-radioGroup-radioItem",
          );

        if (radioItems.length) {
          const selectedItem =
            findSelectedRadioChoice(
              radioItems,
            );

          if (selectedItem) {
            const valueElement =
              selectedItem.querySelector(
                [
                  ".po-optionItemLabel-label",
                  "[class*='po-optionItemLabel-label']",
                ].join(","),
              );

            const value =
              cleanText(
                valueElement
                  ? valueElement
                      .textContent
                  : selectedItem
                      .textContent,
              );

            if (value) {
              selections.push({
                label,
                value,

                displayedPrice:
                  parseDisplayedPrice(
                    selectedItem
                      .textContent,
                  ),

                source:
                  "easify-radio-option",
              });
            }
          }

          return;
        }

        const toggle =
          optionRoot.querySelector(
            ".po-toggle-root",
          );

        if (toggle) {
          const toggleClass =
            String(
              toggle.className || "",
            );

          const isOn =
            toggleClass.includes(
              "po-toggle-on",
            ) ||
            toggle.getAttribute(
              "aria-checked",
            ) === "true" ||
            toggle.getAttribute(
              "data-state",
            ) === "on" ||
            toggle.getAttribute(
              "data-state",
            ) === "checked";

          const switchLabel =
            optionRoot.querySelector(
              ".po-switch-label",
            );

          const visibleSwitchText =
            cleanText(
              switchLabel
                ? switchLabel
                    .textContent
                : "",
            );

          selections.push({
            label,

            value:
              isOn
                ? (
                    visibleSwitchText ||
                    "Yes"
                  )
                : "No",

            displayedPrice: 0,

            source:
              "easify-switch",
          });

          return;
        }

        const textField =
          optionRoot.querySelector(
            [
              "input[type='text']",
              "input[type='number']",
              "input[type='email']",
              "input[type='tel']",
              "textarea",
            ].join(","),
          );

        if (textField) {
          const value =
            cleanText(
              textField.value,
            );

          if (value) {
            selections.push({
              label,
              value,

              displayedPrice:
                parseDisplayedPrice(
                  textField.value,
                ),

              source:
                "easify-text-field",
            });
          }
        }
      });

    return selections;
  }

  function deduplicateSelections(
    selections,
  ) {
    const uniqueSelections = [];
    const seen = new Set();

    selections.forEach(
      (selection) => {
        const label = String(
          selection.label ||
          "Selected option",
        )
          .replace(/\*/g, "")
          .replace(/\s+/g, " ")
          .trim();

        const value = String(
          selection.value || "",
        )
          .replace(/\s+/g, " ")
          .trim();

        if (!value) {
          return;
        }

        const key = [
          normaliseText(label),
          normaliseText(value),
        ].join("::");

        if (seen.has(key)) {
          return;
        }

        seen.add(key);

        uniqueSelections.push({
          label,
          value,

          displayedPrice:
            Number(
              selection.displayedPrice ||
              0,
            ),

          source:
            selection.source || "",
        });
      },
    );

    return uniqueSelections;
  }

  function getProductOptionSets(
    easifyData,
    productHandle,
  ) {
    if (
      !easifyData ||
      !easifyData.optionSets
    ) {
      return [];
    }

    const indexedSetIds =
      easifyData.productIndex &&
      easifyData.productIndex[
        productHandle
      ]
        ? easifyData.productIndex[
            productHandle
          ]
        : [];

    if (indexedSetIds.length) {
      return indexedSetIds
        .map(
          (setId) =>
            easifyData.optionSets[
              setId
            ],
        )
        .filter(Boolean);
    }

    return Object.values(
      easifyData.optionSets,
    ).filter((optionSet) => {
      return (
        Array.isArray(
          optionSet.products,
        ) &&
        optionSet.products.includes(
          productHandle,
        )
      );
    });
  }

  function getOptionMatchScore(
    selectedLabel,
    selectedValue,
    option,
    optionValue,
  ) {
    const selectedLabelNormal =
      normaliseText(
        selectedLabel,
      );

    const selectedValueNormal =
      normaliseText(
        selectedValue,
      );

    const optionLabelNormal =
      normaliseText(
        option.label ||
        option.name,
      );

    const optionValueNormal =
      normaliseText(
        optionValue.label,
      );

    let score = 0;

    if (
      selectedLabelNormal &&
      optionLabelNormal
    ) {
      if (
        selectedLabelNormal ===
        optionLabelNormal
      ) {
        score += 100;
      } else if (
        selectedLabelNormal.includes(
          optionLabelNormal,
        ) ||
        optionLabelNormal.includes(
          selectedLabelNormal,
        )
      ) {
        score += 40;
      }
    }

    if (
      selectedValueNormal ===
      optionValueNormal
    ) {
      score += 200;
    } else if (
      optionValueNormal &&
      selectedValueNormal.includes(
        optionValueNormal,
      )
    ) {
      score += 150;
    } else if (
      selectedValueNormal &&
      optionValueNormal.includes(
        selectedValueNormal,
      )
    ) {
      score += 100;
    }

    return score;
  }

  function matchSelectionToPricing(
    selection,
    optionSets,
  ) {
    let bestMatch = null;
    let bestScore = 0;

    optionSets.forEach(
      (optionSet) => {
        (
          optionSet.options || []
        ).forEach((option) => {
          (
            option.values || []
          ).forEach(
            (optionValue) => {
              const score =
                getOptionMatchScore(
                  selection.label,
                  selection.value,
                  option,
                  optionValue,
                );

              if (
                score > bestScore
              ) {
                bestScore = score;

                bestMatch = {
                  optionSetId:
                    optionSet.id,

                  optionSetTitle:
                    optionSet.title ||
                    "",

                  optionId:
                    option.id,

                  optionLabel:
                    option.label ||
                    option.name ||
                    selection.label,

                  optionValueId:
                    optionValue.id ||
                    "",

                  value:
                    optionValue.label ||
                    selection.value,

                  addOnPricePence:
                    Math.round(
                      Number(
                        optionValue.addOnPrice ||
                        0,
                      ) * 100,
                    ),
                };
              }
            },
          );
        });
      },
    );

    if (
      bestMatch &&
      bestScore >= 100
    ) {
      return bestMatch;
    }

    return null;
  }

  async function getSelectedOptions(
    form,
    productHandle,
  ) {
    const rawSelections =
      deduplicateSelections([
        ...collectPropertySelections(
          form,
        ),

        ...collectVisibleEasifySelections(),
      ]);

    const easifyData =
      await loadEasifyData();

    const optionSets =
      getProductOptionSets(
        easifyData,
        productHandle,
      );

    return rawSelections.map(
      (selection) => {
        const pricingMatch =
          matchSelectionToPricing(
            selection,
            optionSets,
          );

        const addOnPricePence =
          pricingMatch
            ? pricingMatch
                .addOnPricePence
            : Number(
                selection
                  .displayedPrice ||
                0,
              );

        return {
          label:
            pricingMatch
              ? pricingMatch
                  .optionLabel
              : selection.label,

          value:
            pricingMatch
              ? pricingMatch.value
              : selection.value,

          addOnPricePence,

          addOnPriceFormatted:
            addOnPricePence
              ? formatMoney(
                  addOnPricePence,
                )
              : "",

          optionSetId:
            pricingMatch
              ? pricingMatch
                  .optionSetId
              : "",

          optionId:
            pricingMatch
              ? pricingMatch.optionId
              : "",

          optionValueId:
            pricingMatch
              ? pricingMatch
                  .optionValueId
              : "",
        };
      },
    );
  }

  function createConfigurationKey(
    variantId,
    selectedOptions,
  ) {
    const optionKey =
      selectedOptions
        .map((option) => {
          return [
            normaliseText(
              option.label,
            ),

            normaliseText(
              option.value,
            ),
          ].join(":");
        })
        .sort()
        .join("|");

    return `${variantId}::${optionKey}`;
  }

  function getOptionsTotal(
    selectedOptions,
  ) {
    return selectedOptions.reduce(
      (total, option) => {
        return (
          total +
          Number(
            option
              .addOnPricePence ||
            0,
          )
        );
      },
      0,
    );
  }

  function setButtonLoading(
    button,
    loading,
  ) {
    button.disabled = loading;

    button.setAttribute(
      "aria-busy",
      loading
        ? "true"
        : "false",
    );

    const label =
      button.querySelector(
        ".product-add-to-quote-label",
      );

    if (
      label &&
      loading
    ) {
      label.textContent =
        "Adding…";
    }
  }

  function showAddedState(
    button,
  ) {
    const icon =
      button.querySelector(
        ".product-add-to-quote-icon",
      );

    const label =
      button.querySelector(
        ".product-add-to-quote-label",
      );

    button.classList.add(
      "is-added",
    );

    if (icon) {
      icon.textContent = "✓";
    }

    if (label) {
      label.textContent =
        "Added to Quote";
    }

    window.setTimeout(
      () => {
        button.classList.remove(
          "is-added",
        );

        button.disabled = false;

        button.setAttribute(
          "aria-busy",
          "false",
        );

        if (icon) {
          icon.textContent = "+";
        }

        if (label) {
          label.textContent =
            "Add to Quote";
        }
      },
      2500,
    );
  }

  function showErrorState(
    button,
  ) {
    const icon =
      button.querySelector(
        ".product-add-to-quote-icon",
      );

    const label =
      button.querySelector(
        ".product-add-to-quote-label",
      );

    button.disabled = false;

    button.setAttribute(
      "aria-busy",
      "false",
    );

    if (icon) {
      icon.textContent = "!";
    }

    if (label) {
      label.textContent =
        "Please try again";
    }

    window.setTimeout(
      () => {
        if (icon) {
          icon.textContent = "+";
        }

        if (label) {
          label.textContent =
            "Add to Quote";
        }
      },
      2500,
    );
  }

  async function addProductToQuote(
    button,
  ) {
    const form =
      getProductForm(button);

    const variantId =
      getSelectedVariantId(form);

    const quantity =
      getSelectedQuantity(form);

    const handle =
      button.dataset.productHandle;

    if (!variantId) {
      throw new Error(
        "Please select a product option first.",
      );
    }

    const results =
      await Promise.all([
        getProductData(handle),

        getSelectedOptions(
          form,
          handle,
        ),
      ]);

    const product =
      results[0];

    const selectedOptions =
      results[1];

    const variant =
      product.variants.find(
        (productVariant) =>
          Number(
            productVariant.id,
          ) ===
          Number(variantId),
      );

    if (!variant) {
      throw new Error(
        "The selected product variant could not be found.",
      );
    }

    const basePricePence =
      Number(
        variant.price || 0,
      );

    const optionsPricePence =
      getOptionsTotal(
        selectedOptions,
      );

    const estimatedUnitPricePence =
      basePricePence +
      optionsPricePence;

    const configurationKey =
      createConfigurationKey(
        variant.id,
        selectedOptions,
      );

    const quoteItems =
      getQuoteItems();

    const existingItem =
      quoteItems.find(
        (item) =>
          item.configurationKey ===
          configurationKey,
      );

    if (existingItem) {
      existingItem.quantity =
        Number(
          existingItem.quantity ||
          0,
        ) +
        Number(quantity);

      existingItem.selectedOptions =
        selectedOptions;

      existingItem.basePricePence =
        basePricePence;

      existingItem.optionsPricePence =
        optionsPricePence;

      existingItem.estimatedUnitPricePence =
        estimatedUnitPricePence;
    } else {
      quoteItems.push({
        productId:
          Number(product.id),

        variantId:
          Number(variant.id),

        configurationKey,

        handle:
          product.handle,

        title:
          product.title,

        variantTitle:
          (
            variant.title &&
            variant.title !==
              "Default Title"
          )
            ? variant.title
            : "",

        sku:
          variant.sku || "",

        quantity,

        basePricePence,

        optionsPricePence,

        estimatedUnitPricePence,

        price:
          basePricePence,

        selectedOptions,

        url:
          button.dataset
            .productUrl ||
          product.url ||
          "",

        image:
          normaliseImageUrl(
            variant.featured_image,
          ) ||
          normaliseImageUrl(
            product.featured_image,
          ) ||
          button.dataset
            .productImage ||
          "",
      });
    }

    saveQuoteItems(
      quoteItems,
    );
  }

  function updateHeaderQuoteCount(
    items = getQuoteItems(),
  ) {
    const count =
      getQuoteItemCount(items);

    document
      .querySelectorAll(
        "[data-quote-header-count]",
      )
      .forEach((element) => {
        element.textContent =
          String(count);

        element.hidden =
          count < 1;
      });
  }

  document.addEventListener(
    "click",
    async (event) => {
      const button =
        event.target.closest(
          "[data-quote-add]",
        );

      if (!button) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (button.disabled) {
        return;
      }

      setButtonLoading(
        button,
        true,
      );

      try {
        await addProductToQuote(
          button,
        );

        showAddedState(
          button,
        );
      } catch (error) {
        console.error(
          "Unable to add product to quote:",
          error,
        );

        showErrorState(
          button,
        );

        if (error.message) {
          window.alert(
            error.message,
          );
        }
      }
    },
  );

  document.addEventListener(
    "radiotrader:quote-updated",
    (event) => {
      const items =
        event.detail &&
        Array.isArray(
          event.detail.items,
        )
          ? event.detail.items
          : getQuoteItems();

      updateHeaderQuoteCount(
        items,
      );
    },
  );

  window.addEventListener(
    "storage",
    (event) => {
      if (
        event.key ===
        STORAGE_KEY
      ) {
        updateHeaderQuoteCount();
      }
    },
  );

  document.addEventListener(
    "DOMContentLoaded",
    () => {
      updateHeaderQuoteCount();
      loadEasifyData();
    },
  );

  updateHeaderQuoteCount();

  window.RadioTraderQuote = {
    getItems:
      getQuoteItems,

    saveItems:
      saveQuoteItems,

    getItemCount:
      getQuoteItemCount,

    updateHeaderCount:
      updateHeaderQuoteCount,

    loadEasifyData,

    formatMoney,

    clear() {
      saveQuoteItems([]);
    },
  };
})();